import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, 
  Plus, 
  Edit, 
  Loader2,
  Settings,
  Layers,
  Receipt,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ParticipantCostConfigPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [billingItems, setBillingItems] = useState([]);
  const [activeTab, setActiveTab] = useState('categories');
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state for categories
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    sort_order: 1,
    is_active: true
  });
  
  // Form state for billing items
  const [itemForm, setItemForm] = useState({
    category_id: '',
    name: '',
    description: '',
    default_cost: 0,
    cost_type: 'variable',
    allow_override: true,
    sort_order: 1,
    is_active: true
  });

  // Roles allowed to configure (System Admin and Executive only)
  const ADMIN_ROLES = ['system_admin', 'executive'];
  const canConfigure = ADMIN_ROLES.includes(user?.role);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/participant-cost-categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      if (error.response?.status === 403) {
        // Access denied handled in UI
      } else {
        toast.error('Failed to load categories');
      }
    }
  }, []);

  const fetchBillingItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/participant-cost-billing-items`);
      setBillingItems(response.data.billing_items || []);
    } catch (error) {
      console.error('Failed to fetch billing items:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchBillingItems()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchBillingItems]);

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      sort_order: categories.length + 1,
      is_active: true
    });
    setSelectedCategory(null);
  };

  const resetItemForm = () => {
    setItemForm({
      category_id: '',
      name: '',
      description: '',
      default_cost: 0,
      cost_type: 'variable',
      allow_override: true,
      sort_order: 1,
      is_active: true
    });
    setSelectedItem(null);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      sort_order: category.sort_order || 1,
      is_active: category.is_active !== false
    });
    setShowCategoryDialog(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setItemForm({
      category_id: item.category_id,
      name: item.name,
      description: item.description || '',
      default_cost: item.default_cost || 0,
      cost_type: item.cost_type || 'variable',
      allow_override: item.allow_override !== false,
      sort_order: item.sort_order || 1,
      is_active: item.is_active !== false
    });
    setShowItemDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    setSaving(true);
    try {
      if (selectedCategory) {
        await axios.put(`${API}/admin/participant-cost-categories/${selectedCategory.id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${API}/admin/participant-cost-categories`, categoryForm);
        toast.success('Category created successfully');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      toast.error('Please enter a billing item name');
      return;
    }
    if (!itemForm.category_id) {
      toast.error('Please select a category');
      return;
    }

    setSaving(true);
    try {
      if (selectedItem) {
        await axios.put(`${API}/admin/participant-cost-billing-items/${selectedItem.id}`, itemForm);
        toast.success('Billing item updated successfully');
      } else {
        await axios.post(`${API}/admin/participant-cost-billing-items`, itemForm);
        toast.success('Billing item created successfully');
      }
      setShowItemDialog(false);
      resetItemForm();
      fetchBillingItems();
    } catch (error) {
      console.error('Failed to save billing item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save billing item');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  // Access denied view for non-admin users
  if (!canConfigure) {
    return (
      <AppLayout>
        <div className="p-6">
          <Card className="card-shadow">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-12">
                <Lock className="w-16 h-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Access Denied</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  You do not have permission to configure Participant Cost settings.
                  This feature is restricted to System Administrators and Executives.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Participant Cost Configuration
            </h1>
            <p className="text-muted-foreground">
              Configure cost categories and billing items for SNAP E&T participant expenses
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="categories" data-testid="tab-categories">
                <Layers className="w-4 h-4 mr-2" />
                Categories ({categories.length})
              </TabsTrigger>
              <TabsTrigger value="billing-items" data-testid="tab-billing-items">
                <Receipt className="w-4 h-4 mr-2" />
                Billing Items ({billingItems.length})
              </TabsTrigger>
            </TabsList>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-4">
              <Card className="card-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Cost Categories</CardTitle>
                      <CardDescription>
                        Define the main categories for participant costs (e.g., Tuition, Supportive Services)
                      </CardDescription>
                    </div>
                    <Button onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }} data-testid="add-category-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Category
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {categories.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Order</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categories.sort((a, b) => a.sort_order - b.sort_order).map(category => (
                            <TableRow key={category.id}>
                              <TableCell>{category.sort_order}</TableCell>
                              <TableCell className="font-medium">{category.name}</TableCell>
                              <TableCell className="text-muted-foreground">{category.description || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {billingItems.filter(i => i.category_id === category.id).length} items
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {category.is_active !== false ? (
                                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                                ) : (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => handleEditCategory(category)} data-testid={`edit-category-${category.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No categories configured</p>
                      <p className="text-sm mt-1">Click "Add Category" to create the first cost category</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Billing Items Tab */}
            <TabsContent value="billing-items" className="space-y-4">
              <Card className="card-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Billing Items</CardTitle>
                      <CardDescription>
                        Define specific expense items within each category with default costs
                      </CardDescription>
                    </div>
                    <Button onClick={() => { resetItemForm(); setShowItemDialog(true); }} data-testid="add-item-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Billing Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {billingItems.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Category</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Default Cost</TableHead>
                            <TableHead>Cost Type</TableHead>
                            <TableHead>Override</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {billingItems
                            .sort((a, b) => {
                              const catA = categories.find(c => c.id === a.category_id)?.sort_order || 0;
                              const catB = categories.find(c => c.id === b.category_id)?.sort_order || 0;
                              if (catA !== catB) return catA - catB;
                              return (a.sort_order || 0) - (b.sort_order || 0);
                            })
                            .map(item => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <Badge variant="outline">{getCategoryName(item.category_id)}</Badge>
                                </TableCell>
                                <TableCell className="font-medium">{item.name}</TableCell>
                                <TableCell>{formatCurrency(item.default_cost)}</TableCell>
                                <TableCell>
                                  <Badge variant={item.cost_type === 'fixed' ? 'default' : 'secondary'}>
                                    {item.cost_type === 'fixed' ? 'Fixed' : 'Variable'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {item.allow_override !== false ? (
                                    <span className="text-green-600">Yes</span>
                                  ) : (
                                    <span className="text-red-600">No</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.is_active !== false ? (
                                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                                  ) : (
                                    <Badge variant="secondary">Inactive</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button variant="ghost" size="sm" onClick={() => handleEditItem(item)} data-testid={`edit-item-${item.id}`}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No billing items configured</p>
                      <p className="text-sm mt-1">Click "Add Billing Item" to create expense items</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Category Dialog */}
        <Dialog open={showCategoryDialog} onOpenChange={(open) => { if (!open) resetCategoryForm(); setShowCategoryDialog(open); }}>
          <DialogContent data-testid="category-dialog">
            <DialogHeader>
              <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
              <DialogDescription>
                {selectedCategory ? 'Update the category details' : 'Create a new cost category'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category Name *</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Supportive Services"
                  data-testid="category-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description of this category"
                  rows={2}
                  data-testid="category-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    min="1"
                    value={categoryForm.sort_order}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                    data-testid="category-sort-order"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      checked={categoryForm.is_active}
                      onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, is_active: checked }))}
                      data-testid="category-active"
                    />
                    <span className="text-sm">{categoryForm.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { resetCategoryForm(); setShowCategoryDialog(false); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveCategory} disabled={saving} data-testid="save-category-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {selectedCategory ? 'Update Category' : 'Create Category'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Billing Item Dialog */}
        <Dialog open={showItemDialog} onOpenChange={(open) => { if (!open) resetItemForm(); setShowItemDialog(open); }}>
          <DialogContent data-testid="item-dialog">
            <DialogHeader>
              <DialogTitle>{selectedItem ? 'Edit Billing Item' : 'Add Billing Item'}</DialogTitle>
              <DialogDescription>
                {selectedItem ? 'Update the billing item details' : 'Create a new billing item'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={itemForm.category_id} onValueChange={(value) => setItemForm(prev => ({ ...prev, category_id: value }))}>
                  <SelectTrigger data-testid="item-category">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.is_active !== false).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={itemForm.name}
                  onChange={(e) => setItemForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Transportation"
                  data-testid="item-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={itemForm.description}
                  onChange={(e) => setItemForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows={2}
                  data-testid="item-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Cost</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={itemForm.default_cost}
                    onChange={(e) => setItemForm(prev => ({ ...prev, default_cost: parseFloat(e.target.value) || 0 }))}
                    data-testid="item-default-cost"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cost Type</Label>
                  <Select value={itemForm.cost_type} onValueChange={(value) => setItemForm(prev => ({ ...prev, cost_type: value }))}>
                    <SelectTrigger data-testid="item-cost-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed (Qty x Unit Cost)</SelectItem>
                      <SelectItem value="variable">Variable (Manual Entry)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    min="1"
                    value={itemForm.sort_order}
                    onChange={(e) => setItemForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                    data-testid="item-sort-order"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Allow Override</Label>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      checked={itemForm.allow_override}
                      onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, allow_override: checked }))}
                      data-testid="item-allow-override"
                    />
                    <span className="text-sm">{itemForm.allow_override ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={itemForm.is_active}
                    onCheckedChange={(checked) => setItemForm(prev => ({ ...prev, is_active: checked }))}
                    data-testid="item-active"
                  />
                  <span className="text-sm">{itemForm.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { resetItemForm(); setShowItemDialog(false); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveItem} disabled={saving} data-testid="save-item-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {selectedItem ? 'Update Item' : 'Create Item'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default ParticipantCostConfigPage;
